import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Chart from 'react-apexcharts';
import Loader from '../../component/common/Loader';
import { formatBigNumber, lastNDaysLabels } from '../SleepHub/sleepHubApi';
import { formatMoney, formatMoneyExact } from './marketplaceHubApi';
import {
  StatCard,
  Panel,
  PanelEmpty,
  PanelTable,
  MiniDonutCard,
  PANEL_ROW_CLASS,
  Chip,
  EntityHeader,
  BackLink,
  STAT_ICONS,
  CHART_BASE,
  CHART_COLORS,
  CHART_GRID,
  CHART_GRID_NO_X,
  AREA_FILL,
  toneChip,
  paletteAt
} from '../../component/common/designSystem';
import { ORDER_STATUS_COLORS, orderStatusChipClass } from './marketplaceTheme';

/**
 * Marketplace → User profile.
 * One person's redemption life: what they spent, how it resolved, which
 * brands they favour and their recent orders.
 *
 * Reused by the company panel via the `fetcher` / `backPath` / `backLabel`
 * props, following the same shape as the sleep/breathwork/habit detail pages.
 */
const MarketplaceUserDetail = ({ fetcher, backPath, backLabel }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('userId');

  const [loader, setLoader] = useState(true);
  const [detail, setDetail] = useState(null);

  useEffect(() => {
    if (!userId) {
      navigate(backPath, { replace: true });
      return;
    }
    let mounted = true;
    setLoader(true);
    fetcher(userId).then((data) => {
      if (!mounted) return;
      setDetail(data);
      setLoader(false);
    });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, fetcher]);

  const user = detail?.user;
  const ordersPerDay = detail?.ordersPerDay || [];
  const valuePerDay = detail?.valuePerDay || [];
  const statusSplit = detail?.statusSplit || [];
  const topBrands = detail?.topBrands || [];
  const recentOrders = detail?.recentOrders || [];

  /**
   * Orders and spend on one chart, two axes — the same pairing the section
   * dashboard uses, so a person's page reads like a slice of the whole.
   */
  const trendOptions = {
    chart: { ...CHART_BASE, type: 'line' },
    colors: [CHART_COLORS.brand, CHART_COLORS.teal],
    stroke: { curve: 'smooth', width: [2, 2], dashArray: [0, 4] },
    fill: { type: ['gradient', 'solid'], gradient: AREA_FILL.gradient },
    dataLabels: { enabled: false },
    grid: CHART_GRID,
    legend: { position: 'top', horizontalAlign: 'right' },
    xaxis: { categories: lastNDaysLabels(ordersPerDay.length), tickAmount: 6 },
    yaxis: [
      { seriesName: 'Orders', labels: { formatter: (val) => formatBigNumber(val) } },
      {
        seriesName: 'Spend',
        opposite: true,
        labels: { formatter: (val) => formatMoney(val) }
      }
    ],
    tooltip: {
      shared: true,
      y: [
        { formatter: (val) => `${val.toLocaleString()} orders` },
        { formatter: (val) => formatMoneyExact(val) }
      ]
    }
  };

  const brandOptions = {
    chart: { ...CHART_BASE, type: 'bar' },
    colors: topBrands.map((brand, index) => paletteAt(index)),
    plotOptions: {
      bar: { horizontal: true, borderRadius: 4, barHeight: '60%', distributed: true }
    },
    dataLabels: { enabled: true, style: { fontSize: '12px' } },
    legend: { show: false },
    grid: CHART_GRID_NO_X,
    xaxis: { categories: topBrands.map((brand) => brand.name) },
    tooltip: { y: { formatter: (val) => `${val.toLocaleString()} orders` } }
  };

  return (
    <div>
      <Helmet>
        <meta charSet="utf-8" />
        <title>User Marketplace Profile | Shoorah</title>
      </Helmet>
      {loader ? <Loader /> : null}

      <BackLink label={backLabel} onClick={() => navigate(backPath)} />

      {user && (
        <EntityHeader
          className="mt-4"
          image={user.user_profile}
          name={user.name}
          subtitle={user.email}
          action={
            <Chip className={toneChip('brand')}>
              {user.accountType === 'B2B' ? `B2B — ${user.company}` : 'B2C'}
            </Chip>
          }
        />
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-4 mt-6">
        <StatCard
          icon={STAT_ICONS.catalog}
          title="Total Orders"
          value={user ? user.totalOrders : '-'}
        />
        <StatCard
          icon={STAT_ICONS.rating}
          title="Total Spend"
          value={user ? formatMoney(user.totalValue) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.score}
          title="Avg Order"
          value={user ? formatMoney(user.avgOrderValue) : '-'}
        />
        <StatCard
          icon={STAT_ICONS.completions}
          title="Fulfilled"
          value={user ? `${user.successRate}%` : '-'}
        />
        <StatCard
          icon={STAT_ICONS.plays}
          title="Sent as Gifts"
          value={user ? user.giftedOrders : '-'}
        />
        <StatCard
          icon={STAT_ICONS.audioLibrary}
          title="Favourite Brand"
          value={user?.favouriteBrand || '-'}
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6 mt-6">
        <Panel className="xl:col-span-2" title="Orders & Spend" subtitle="Per day — last 30 days">
          {ordersPerDay.some((value) => value > 0) ? (
            <Chart
              type="line"
              height={320}
              width="100%"
              series={[
                { name: 'Orders', type: 'area', data: ordersPerDay },
                { name: 'Spend', type: 'line', data: valuePerDay }
              ]}
              options={trendOptions}
            />
          ) : (
            <PanelEmpty>No orders in the last 30 days.</PanelEmpty>
          )}
        </Panel>

        <MiniDonutCard
          title="Order Status"
          subtitle="How their orders resolved"
          data={statusSplit}
          colors={ORDER_STATUS_COLORS}
          unit="orders"
        />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mt-6">
        <Panel title="Favourite Brands" subtitle="Where their spend goes">
          {topBrands.length > 0 ? (
            <Chart
              type="bar"
              height={300}
              width="100%"
              series={[{ name: 'Orders', data: topBrands.map((brand) => brand.orders) }]}
              options={brandOptions}
            />
          ) : (
            <PanelEmpty>No orders recorded yet.</PanelEmpty>
          )}
        </Panel>

        <Panel title="Recent Orders" subtitle="Most recent 20">
          {recentOrders.length > 0 ? (
            <PanelTable
              head={
                <>
                  <th className="py-2 pr-4 font-medium">Date</th>
                  <th className="py-2 pr-4 font-medium">Brand</th>
                  <th className="py-2 pr-4 font-medium text-right">Value</th>
                  <th className="py-2 font-medium text-right">Status</th>
                </>
              }
            >
              {recentOrders.map((order, index) => (
                <tr key={order.id || index} className={PANEL_ROW_CLASS}>
                  <td className="py-2 pr-4">{order.date}</td>
                  <td className="py-2 pr-4">
                    {order.brand}
                    {order.quantity > 1 ? (
                      <span className="text-gray-400"> ×{order.quantity}</span>
                    ) : null}
                  </td>
                  <td className="py-2 pr-4 text-right">{formatMoneyExact(order.value)}</td>
                  <td className="py-2 text-right">
                    <Chip className={orderStatusChipClass(order.status)}>{order.status}</Chip>
                  </td>
                </tr>
              ))}
            </PanelTable>
          ) : (
            <PanelEmpty>This person has not redeemed anything yet.</PanelEmpty>
          )}
        </Panel>
      </div>
    </div>
  );
};

MarketplaceUserDetail.propTypes = {
  fetcher: PropTypes.func.isRequired,
  backPath: PropTypes.string.isRequired,
  backLabel: PropTypes.string
};

export default MarketplaceUserDetail;
